// src/components/sad/RequesterSadPopup.tsx
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import gorillamensaje from '../../imagenes/Gorillamensaje.png';
import { useAuth } from '../../context/AuthContext';
import {
  hasSadRetryAttempt,
  hasAnySadRetryAttempt,
  consumeSadRetryAttempt,
  consumeFirstSadRetryAttempt,
  saveSadRetryAttempt,
} from '../../utils/sadRetryStore';
import { useReservaMutation } from '../../hooks/useReservationMutation'; // ← NUEVO: usa el mismo hook que el formulario

type Notif = {
  id: number;
  event: 'SAD_ACCEPTED' | 'SAD_REJECTED' | 'SAD_APPLICATION_FAILED' | 'SAD_EXPIRED' | string;
  payload?: any;
  createdAt: string;
  priority?: 'P0' | 'P1' | 'P2';
  requiresAction?: boolean;
};

const POLL_MS = 15000;
const SAD_EVENTS = new Set([
  'SAD_ACCEPTED',
  'SAD_REJECTED',
  'SAD_APPLICATION_FAILED',
  'SAD_EXPIRED',
]);

export default function RequesterSadPopup({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation<'translation'>();
  const [open, setOpen] = useState(false);
  const [notif, setNotif] = useState<Notif | null>(null);
  const showingIdRef = useRef<number | null>(null);
  const fetching = useRef(false);
  const { user } = useAuth();
  const ownerId = (user as any)?.id as number | undefined;

  // ← hook de reservas
  const { mutateAsync } = useReservaMutation();

  const load = useCallback(async () => {
    if (!enabled || open || fetching.current) return;
    fetching.current = true;
    try {
      const { data } = await api.get<{ rows: Notif[]; total: number }>('/notifications', {
        params: { unread: true, channel: 'IN_APP', take: 20, tab: 'all' },
      });
      const candidates = (data?.rows ?? []).filter((n) => SAD_EVENTS.has(n.event));
      if (!candidates.length) return;
      candidates.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      const next = candidates.find((n) => n.id !== showingIdRef.current);
      if (!next) return;

      showingIdRef.current = next.id;
      setNotif(next);
      setOpen(true);

      // Toasts traducidas
      if (next.event === 'SAD_ACCEPTED') toast.success(t('sadPopup.toast.accepted'));
      else if (next.event === 'SAD_REJECTED') toast.info(t('sadPopup.toast.rejected'));
      else if (next.event === 'SAD_APPLICATION_FAILED')
        toast.error(t('sadPopup.toast.applicationFailed'));
      else if (next.event === 'SAD_EXPIRED') toast.info(t('sadPopup.toast.expired'));
    } finally {
      fetching.current = false;
    }
  }, [enabled, open, t]);

  useEffect(() => {
    if (!enabled) return;
    load();
    const loop = () => {
      if (!open) load();
      timer.current = window.setTimeout(loop, POLL_MS);
    };
    const timer = {
      current: window.setTimeout(loop, POLL_MS) as number | null,
    };
    const onVis = () => document.visibilityState === 'visible' && !open && load();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, load, open]);

  const markRead = async (id?: number) => {
    if (!id) return;
    try {
      await api.patch(`/notifications/${id}/read`);
    } catch {}
  };

  const handleClose = async () => {
    await markRead(notif?.id);
    setOpen(false);
    setNotif(null);
  };

  if (!notif) return null;

  const { payload } = notif;

  const title =
    notif.event === 'SAD_ACCEPTED'
      ? t('sadPopup.titles.accepted')
      : notif.event === 'SAD_REJECTED'
        ? t('sadPopup.titles.rejected')
        : notif.event === 'SAD_APPLICATION_FAILED'
          ? t('sadPopup.titles.applicationFailed')
          : t('sadPopup.titles.expired');

  const tipo = payload?.tipo?.toUpperCase?.();
  const periodoIni = payload?.periodoInicio ?? payload?.periodoFin;
  const periodoFin = payload?.periodoFin;
  const profesor = payload?.profesorNombre;

  const profIdFromNotif: number | undefined =
    payload?.profesorUserId ?? payload?.profesorId ?? payload?.teacherUserId ?? payload?.idProfesor;

  const canRetry = notif.event === 'SAD_ACCEPTED';

  const handleCreateNow = async () => {
    if (!ownerId) return;

    // Intentar recuperar intento específico o el primero que haya
    const attempt = profIdFromNotif
      ? consumeSadRetryAttempt(ownerId, profIdFromNotif)
      : consumeFirstSadRetryAttempt(ownerId);

    await markRead(notif?.id);

    if (!attempt) {
      // Fallback: Si no hay datos para reintentar (SAD manual o datos perdidos),
      // avisamos al usuario para que proceda manualmente, pero cerramos el popup de éxito.
      toast.info(t('sadPopup.retry.none', { defaultValue: 'No hay intento guardado.' }));
      return;
    }

    try {
      const payload = attempt.payload || {};
      // Heurística simple: si trae campos de recurrencia → serie
      const isRecurrent =
        payload?.recurre === true ||
        payload?.patron != null ||
        payload?.diasSemana != null ||
        payload?.intervalo != null ||
        payload?.repeticiones != null;

      await mutateAsync({
        mode: isRecurrent ? 'recurrente' : 'create',
        data: payload,
      });

      toast.success(
        t('sadPopup.retry.ok', {
          defaultValue: 'Reserva creada correctamente.',
        }),
      );
      setOpen(false);
      setNotif(null);
    } catch (e) {
      // reponemos el intento para no perderlo
      saveSadRetryAttempt(ownerId, attempt.profesorUserId, attempt.proyectoId!, attempt.payload);
      toast.error(
        t('sadPopup.retry.fail', {
          defaultValue: 'No se pudo crear la reserva. Revisa solapes o vuelve a intentarlo.',
        }),
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="sad-status-title"
    >
      <DialogTitle id="sad-status-title">
        {title}{' '}
        {tipo ? (
          <Chip
            size="small"
            label={tipo.toUpperCase()}
            sx={{
              fontSize: '0.75rem',
              height: 18,
              px: 0.5,
              bgcolor: '#fcf1ad',
              color: 'rgb(84,84,72)',
              borderRadius: '6px',
              ml: 0.75,
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1,
              verticalAlign: 'middle',
              position: 'relative',
              top: '-1px', // súbelo un pelín para centrarlo con el título
            }}
          />
        ) : null}
      </DialogTitle>

      <DialogContent dividers>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Columna imagen */}
          <img
            src={gorillamensaje}
            alt={String(t('sadPopup.alt.gorillaMessage'))}
            style={{ width: '40%', height: 'auto' }}
          />

          {/* Columna contenido */}
          <Stack spacing={1.5} flex={1}>
            <Typography variant="body1">{t('sadPopup.body.headline')}</Typography>
            <Typography variant="body2">{t('sadPopup.body.details')}</Typography>

            {profesor && (
              <Typography variant="body2">
                <b>{t('sadPopup.labels.teacher')}:</b> {profesor}
              </Typography>
            )}

            {(periodoIni || periodoFin) && (
              <Typography variant="body2">
                <b>{t('sadPopup.labels.period')}:</b>{' '}
                {periodoIni === periodoFin
                  ? (periodoIni ?? '—')
                  : `${periodoIni ?? '—'} - ${periodoFin ?? '—'}`}
              </Typography>
            )}

            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {t('sadPopup.labels.receivedAt')}: {dayjs(notif.createdAt).format('DD/MM/YYYY HH:mm')}
            </Typography>

            {notif.event === 'SAD_APPLICATION_FAILED' && (
              <>
                <Divider />
                <Typography variant="body2" color="error">
                  {t('sadPopup.notes.applicationFailed')}
                </Typography>
              </>
            )}

            {notif.event === 'SAD_EXPIRED' && (
              <>
                <Divider />
                <Typography variant="body2">{t('sadPopup.notes.expired')}</Typography>
              </>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          sx={{
            color: '#21a598',
            borderColor: '#21a598',
            textTransform: 'uppercase',
            fontWeight: 400,
            boxShadow: 'none',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(33,165,152,0.08)',
              borderColor: '#1b887f',
              boxShadow: 'none',
            },
          }}
          onClick={handleClose}
        >
          {t('sadPopup.buttons.close')}
        </Button>

        {canRetry && (
          <Button
            variant="contained"
            sx={{
              ml: 1,
              backgroundColor: '#21a598',
              boxShadow: 'none',
              textTransform: 'uppercase',
              '&:hover': { backgroundColor: '#1a8578', boxShadow: 'none' },
            }}
            onClick={handleCreateNow}
          >
            {t('sadPopup.buttons.createNow', {
              defaultValue: 'Crear la reserva ahora',
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
